import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Checkbox, Col, Divider, Row, Space, Switch } from 'antd'
import { PrimaryButton } from 'Components/button'
import {
  isFormRequired,
  isFormError,
  formValidation,
  parseNumberToInt,
  getPriceConfigsMenuSelect,
  validateEmail,
} from 'helpers/format'
import {
  CreateSaleOrderCustomerReq,
  SaleOrderCustomerDetailReq,
  UpdateSaleOrderCustomerReq,
} from 'Store/saleorder-customer/types'
import {
  createSaleOrderCustomerAPI,
  getSaleOrderCustomerDetailAPI,
  updateSaleOrderCustomerAPI,
} from 'Store/saleorder-customer/api'
import {
  selectSaleOrderCustomerActionEvent,
  selectSaleOrderCustomerDetail,
  selectSaleOrderCustomerDetailEvent,
} from 'Store/saleorder-customer/selector'
import { selectCurrentUser } from 'Store/user/selector'
import { LabelItem, AppInput, AppSelect, MultiUploadForm } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/saleorder-customer/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { selectDatas } from 'Store/config/selector'
import LinkText from 'Components/text/LinkText'
import { getStaffPermission } from 'helpers/account'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
  disabled?: boolean
}

const SaleOrderCustomersDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detail,
    detailEvent,
    configs,
    disabled = false,
  } = props
  const isCreateMode = mode === 'create'
  const requireFeild = ['name', 'mobile', 'address', 'type']
  const initForm = {
    address: '',
    credit_limit: 0,
    credit_term: 0,
    reference_docs: [],
    email: '',
    is_tax: false,
    mobile: '',
    name: '',
    status: 'active',
    tax_address: '',
    tax_mobile: '',
    tax_name: '',
    tax_number: '',
    type: '',
    is_member: false,
  } as CreateSaleOrderCustomerReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const [creditActive, setCreditActive] = useState(false)
  const hasMemberPermission = true
  const dispatch = useDispatch()

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)

    if (formData.email) {
      if (!validateEmail(formData.email)) {
        error.push('email_format')
      }
    }
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onCreate = (): void => {
    if (!validateForm()) return
    const req: CreateSaleOrderCustomerReq = {
      ...formData,
    }
    dispatch(
      createSaleOrderCustomerAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''),
    )
  }

  const onGetDetail = (): void => {
    const req: SaleOrderCustomerDetailReq = {
      id,
    }
    dispatch(
      getSaleOrderCustomerDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''),
    )
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    const req: UpdateSaleOrderCustomerReq = {
      ...formData,
      id,
    }
    dispatch(
      updateSaleOrderCustomerAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''),
    )
  }

  const onCopyCustomerDataToTax = () => {
    setFormData({
      ...formData,
      tax_name: formData.name,
      tax_mobile: formData?.mobile || '',
      tax_address: formData.address,
    })
  }

  useEffect(() => {
    // clear form data when user cloase drawer
    if (!visible) {
      setFormData(cloneDeep(initForm))
      setFormError([])
      setCreditActive(false)
    }

    // get detail on "edit" mode
    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
    } else {
      setFormData(cloneDeep(initForm))
    }
  }, [visible, id])

  useEffect(() => {
    if (mode === 'edit' && id > 0) {
      const form = {
        ...detail,
        status: 'active',
        reference_docs: detail?.reference_docs || [],
        is_member: detail?.is_member || false,
      } as CreateSaleOrderCustomerReq
      if ((detail?.credit_limit || 0) > 0 || (detail?.credit_term || 0) > 0) {
        setCreditActive(true)
      }
      setFormData({ ...form })
    }
  }, [detail, visible])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const onUpdateData = (val: any, key: string) => {
    if (key === 'mobile' && val.length > 10) {
      return
    }

    if (key === 'tax_mobile' && val.length > 10) {
      return
    }

    if (key === 'tax_number' && val.length > 13) {
      return
    }

    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const handleToggleStatus = (val: boolean) => {
    setCreditActive(val)
    setFormData({
      ...formData,
      credit_limit: 0,
      credit_term: 0,
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const title = isCreateMode ? 'เพิ่มข้อมูลลูกค้า/คู่ค้า' : 'แก้ไขข้อมูลลูกค้า/คู่ค้า'

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      {isCreateMode ? (
        <PrimaryButton text="ยืนยันการสร้าง" onClick={onCreate} />
      ) : (
        <PrimaryButton text="ยืนยันการแก้ไขข้อมูล" onClick={onUpdate} />
      )}
    </Space>
  )

  return (
    <DetailDrawer
      title={title}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={disabled ? null : FooterContent}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={16}>
        <Col md={12} xs={24}>
          <AppInput
            name="name"
            label="ชื่อลูกค้า/คู่ค้า"
            placeHolder="ระบุชื่อ-นามสกุล"
            isRequired={isRequired('name')}
            isError={isError('name')}
            value={formData.name}
            onChange={onUpdateData}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="mobile"
            label="เบอร์โทรศัพท์"
            isRequired={isRequired('mobile')}
            isError={isError('mobile')}
            value={formData.mobile}
            onChange={onUpdateData}
            resProps={{ disabled: !isCreateMode || disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="email"
            label="อีเมล"
            errorMsg={isError('email_format') ? 'รูปแบบอีเมลให้ถูกต้อง' : 'อีเมล'}
            isRequired={isRequired('email')}
            isError={isError('email') || isError('email_format')}
            value={formData.email}
            onChange={onUpdateData}
            specialRequired={true}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            label="ที่อยู่"
            isRequired={isRequired('address')}
            isError={isError('address')}
            name="address"
            value={formData.address}
            onChange={onUpdateData}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppSelect
            name="type"
            label="ประเภทลูกค้า"
            placeHolder="เลือกประเภทลูกค้า"
            isRequired={isRequired('type')}
            isError={isError('type')}
            value={formData?.type}
            onChange={onUpdateData}
            menu={getPriceConfigsMenuSelect(configs?.value?.price_labels || [], 'value', 'name')}
            allowClear={false}
            disabled={disabled}
          />
        </Col>

        <Divider />
        <Col md={24} xs={24}>
          <Row justify="space-between">
            <Col>
              <Title>ข้อมูลสำหรับออกใบกำกับภาษีรูปแบบเต็ม</Title>
            </Col>
            <Col>
              <MyLinkText onClick={onCopyCustomerDataToTax}>คัดลอกจากข้อมูลลูกค้า</MyLinkText>
            </Col>
          </Row>
          <br />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="tax_name"
            label="ชื่อ"
            placeHolder="ระบุชื่อ-นามสกุล"
            isRequired={isRequired('tax_name')}
            isError={isError('tax_name')}
            value={formData.tax_name}
            onChange={onUpdateData}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="tax_number"
            label="เลขประจำตัวผู้เสียภาษี"
            isRequired={isRequired('tax_number')}
            isError={isError('tax_number')}
            value={formData.tax_number}
            onChange={onUpdateData}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="tax_mobile"
            label="เบอร์โทรศัพท์"
            isRequired={isRequired('tax_mobile')}
            isError={isError('tax_mobile')}
            value={formData.tax_mobile}
            onChange={onUpdateData}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            label="ที่อยู่"
            isRequired={isRequired('tax_address')}
            isError={isError('tax_address')}
            name="tax_address"
            value={formData.tax_address}
            onChange={onUpdateData}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Divider />
        <Col md={24} xs={24}>
          <Title>ข้อมูลเครดิต</Title>
        </Col>
        <Col md={24} xs={24}>
          <SwitchMargin>
            <Switch checked={creditActive} onChange={handleToggleStatus} disabled={disabled} />
            &nbsp;
            <LabelItem label={`ตั้งค่าเครดิต : ${creditActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}`} />
          </SwitchMargin>
        </Col>
        {creditActive && (
          <Col md={12} xs={24}>
            <AppInput
              name="credit_term"
              label="ระยะเวลาชำระเงิน"
              type="number"
              isRequired={isRequired('credit_term')}
              isError={isError('credit_term')}
              value={formData.credit_term ? parseNumberToInt(formData.credit_term) : ''}
              onChange={onUpdateData}
              resProps={{ suffix: 'วัน', disabled: disabled }}
            />
          </Col>
        )}
        {creditActive && (
          <Col md={12} xs={24}>
            <AppInput
              name="credit_limit"
              label="จำนวนเครดิต"
              type="number"
              isRequired={isRequired('credit_limit')}
              isError={isError('credit_limit')}
              value={formData.credit_limit ? parseNumberToInt(formData.credit_limit) : ''}
              onChange={onUpdateData}
              resProps={{ suffix: 'บาท', disabled: disabled }}
            />
          </Col>
        )}

        <Col md={24} xs={24}>
          <MultiUploadForm
            token={currentUser.accessToken}
            onUpdateData={onUpdateData}
            files={formData.reference_docs}
            folder="sale-order-customers"
            listType="text"
            isPrivate={true}
            name="reference_docs"
            description="ขนาดไม่เกิน 5MB ต่อไฟล์ (.png .jpg .pdf)"
            isRequired={isRequired('reference_docs')}
            isError={isError('reference_docs')}
            label="เอกสาร"
            readOnly={disabled}
          />
        </Col>
        {hasMemberPermission && (
          <Col span={24}>
            <Checkbox
              onChange={e => onUpdateData(e.target.checked, 'is_member')}
              disabled={!isCreateMode && detail?.is_member === true}
              checked={formData.is_member}
            >
              <MyLinkText>ยินยอมสมัครเป็นสมาชิกของร้านค้า</MyLinkText>
            </Checkbox>
          </Col>
        )}
      </Row>
    </DetailDrawer>
  )
}

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const MyLinkText = styled(LinkText)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const SwitchMargin = styled.div`
  margin: 20px 0;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectSaleOrderCustomerActionEvent(state),
    detail: selectSaleOrderCustomerDetail(state),
    detailEvent: selectSaleOrderCustomerDetailEvent(state),
    configs: selectDatas(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(SaleOrderCustomersDetail)
